// src/Components/ImportMarkers/ImportMarkersPanel.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';

// Import sub-components
import ImportKml from './ImportKml';
import ImportExcel from './ImportExcel';
import ArchivedBatches from './ArchivedBatches';
import CheckBatches from './CheckBatches';
import DataIntegrity from './DataIntegrity';

// Import Custom Components
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

// Import Image
import AdminBg from '../../assets/images/AdminBackground.png';

export default function ImportMarkersPanel({ onClose }) {
  // Set initial tab to Excel since it is now the first item in the list
  const [activeTab, setActiveTab] = useState('IMPORT_EXCEL');

  // --- STATE FOR DIRTY CHECKING ---
  // Isolate dirty states so mounted components don't overwrite each other
  const [excelDirty, setExcelDirty] = useState(false);
  const [kmlDirty, setKmlDirty] = useState(false);
  const [hasKmlQueue, setHasKmlQueue] = useState(false);

  const isDirty = excelDirty || kmlDirty;

  // --- STATE FOR ACTION WARNINGS ---
  const [showCloseDialog, setShowCloseDialog] = useState(false);

  // --- MINIMIZE & MAXIMIZE STATE ---
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  // --- DRAG AND DROP STATE ---
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDraggingState, setIsDraggingState] = useState(false);
  const isDraggingRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });

  const panelRef = useRef(null);

  // Fortified focus grab with a timeout to ensure paint completion
  useEffect(() => {
    const focusTimer = setTimeout(() => {
      if (panelRef.current) {
        panelRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(focusTimer);
  }, []);

  // --- DRAG LOGIC ---
  const handlePointerDown = (e) => {
    // Ignore drag if clicking inside a button (like the close X)
    if (e.target.closest('button')) return;
    // Disable dragging when maximized
    if (isMaximized) return;

    isDraggingRef.current = true;
    setIsDraggingState(true);
    startPosRef.current = {
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    };
    // Prevent accidental text selection while dragging
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const handlePointerMove = (e) => {
      if (!isDraggingRef.current) return;

      let newX = e.clientX - startPosRef.current.x;
      let newY = e.clientY - startPosRef.current.y;

      // Prevent panel from being dragged completely off the top of the screen
      if (newY < -120) newY = -120;

      setOffset({
        x: newX,
        y: newY,
      });
    };

    const handlePointerUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setIsDraggingState(false);
      // Restore text selection
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [offset.x, offset.y]);

  // --- HANDLERS ---
  const handleCloseAttempt = useCallback(() => {
    if (isDirty || hasKmlQueue) {
      setShowCloseDialog(true);
    } else {
      onClose();
    }
  }, [isDirty, hasKmlQueue, onClose]);

  const toggleMaximize = (e) => {
    e.stopPropagation();
    setIsMaximized((prev) => !prev);
  };

  // --- ESCAPE KEY LOGIC ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.defaultPrevented) return;

      // Ignore the Escape key entirely if the user is typing in a text field
      if (
        e.key === 'Escape' &&
        document.activeElement &&
        (document.activeElement.tagName === 'INPUT' ||
          document.activeElement.tagName === 'TEXTAREA' ||
          document.activeElement.isContentEditable)
      ) {
        return;
      }

      if (e.key === 'Escape' && !showCloseDialog && !isMinimized) {
        // Stop the event from triggering anything else if we catch it
        e.preventDefault();
        e.stopPropagation();
        handleCloseAttempt();
      }
    };

    // Use the bubbling phase so child dialogs and MUI popovers can intercept and stop propagation first
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showCloseDialog, isMinimized, handleCloseAttempt]);

  const handleTabChange = (newTab) => {
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  };

  const handleConfirmAction = () => {
    setShowCloseDialog(false);
    onClose();
  };

  const handleCancelAction = () => {
    setShowCloseDialog(false);
  };

  // --- RENDER CONTENT ---
  const renderContent = () => {
    // Keep all components mounted, but hide them when inactive
    // This ensures no data is lost during tab navigation
    return (
      <>
        <Box
          sx={{
            display: activeTab === 'IMPORT_EXCEL' ? 'flex' : 'none',
            flexDirection: 'column',
            height: '100%',
          }}>
          <ImportExcel
            setIsDirty={setExcelDirty}
            kmlHasData={kmlDirty}
            onMinimize={() => setIsMinimized(true)}
          />
        </Box>
        <Box
          sx={{
            display: activeTab === 'IMPORT_KML' ? 'flex' : 'none',
            flexDirection: 'column',
            height: '100%',
          }}>
          <ImportKml
            setIsDirty={setKmlDirty}
            setHasKmlQueue={setHasKmlQueue}
            excelHasData={excelDirty}
            onMinimize={() => setIsMinimized(true)}
          />
        </Box>
        <Box
          sx={{
            display: activeTab === 'ARCHIVED_BATCHES' ? 'flex' : 'none',
            flexDirection: 'column',
            height: '100%',
          }}>
          <ArchivedBatches />
        </Box>
        <Box
          sx={{
            display: activeTab === 'CHECK_BATCHES' ? 'flex' : 'none',
            flexDirection: 'column',
            height: '100%',
          }}>
          <CheckBatches />
        </Box>
        <Box
          sx={{
            display: activeTab === 'DATA_INTEGRITY' ? 'flex' : 'none',
            flexDirection: 'column',
            height: '100%',
          }}>
          <DataIntegrity />
        </Box>
      </>
    );
  };

  // --- MENU ITEMS ---
  const menuItems = [
    { id: 'IMPORT_EXCEL', label: 'Batch Import' },
    { id: 'IMPORT_KML', label: 'Import KML/KMZ' },
    { id: 'ARCHIVED_BATCHES', label: 'Archived Batches' },
    { id: 'CHECK_BATCHES', label: 'Check Batches' },
    { id: 'DATA_INTEGRITY', label: 'Data Integrity Check' },
  ];

  return (
    <>
      {/* RESTORE BUTTON (Shows only when minimized) */}
      {isMinimized && (
        <Box sx={{ position: 'fixed', bottom: 24, left: 24, zIndex: 100001 }}>
          <Button
            variant='contained'
            onClick={() => setIsMinimized(false)}
            sx={{
              backgroundColor: '#1976d2',
              color: '#fff',
              fontWeight: 'bold',
              borderRadius: '8px',
              padding: '10px 20px',
              boxShadow: 3,
              '&:hover': { backgroundColor: '#115293' },
            }}>
            RESTORE IMPORT PANEL
          </Button>
        </Box>
      )}

      {/* AMENDED: Top Bar Blocker (Active only when minimized) - Updated to 15% opacity */}
      {isMinimized && (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            // Catches the click so it doesn't pass through to the underlying menu
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '96px', // Matches the exact height of the TopBar
            zIndex: 100000, // High enough to block interactions
            cursor: 'not-allowed',
            backgroundColor: 'rgba(0, 0, 0, 0.15)', // 15% tint for slightly more presence
          }}
          title='Please restore or close the Import Panel to access the menu.'
        />
      )}

      {/* 1. Static Backdrop Overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'rgba(0,0,0,0.2)',
          zIndex: 100000,
          cursor: 'default',
          display: isMinimized ? 'none' : 'block',
        }}
      />

      {/* 2. Moveable Panel */}
      <Box
        sx={{
          position: 'fixed',
          top: isMaximized ? 0 : '120px',
          left: isMaximized ? 0 : '50%',
          transform: isMaximized
            ? 'none'
            : `translate(calc(-50% + ${offset.x}px), ${offset.y}px)`,
          zIndex: 100001,
          display: isMinimized ? 'none' : 'block',
          width: isMaximized ? '100vw' : 'auto',
          height: isMaximized ? '100vh' : 'auto',
        }}>
        <Paper
          ref={panelRef}
          tabIndex={-1}
          elevation={0}
          sx={{
            width: isMaximized ? '100vw' : '80vw',
            height: isMaximized ? '100vh' : '80vh',
            borderRadius: isMaximized ? 0 : '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'white',
            border: isMaximized ? 'none' : '1px solid rgba(0,0,0,0.1)',
            boxShadow: isMaximized ? 'none' : '8px 0px 12px -1px rgba(0,0,0,0.5)',
            outline: 'none',
          }}>
          {/* HEADER (Drag Handle) */}
          <Box
            onPointerDown={handlePointerDown}
            onDoubleClick={toggleMaximize}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 0.5,
              minHeight: '40px',
              borderBottom: '1px solid black',
              backgroundImage: `linear-gradient(rgba(184, 231, 253, .85), rgba(184, 231, 253, .85)), url(${AdminBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              color: 'white',
              flexShrink: 0,
              cursor: isMaximized ? 'default' : isDraggingState ? 'grabbing' : 'grab',
            }}>
            <Typography
              variant='h6'
              fontWeight='bold'
              sx={{
                fontSize: '1.2rem',
                color: 'black',
                pointerEvents: 'none',
              }}>
              Import Markers
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {(activeTab === 'IMPORT_KML' || activeTab === 'IMPORT_EXCEL') && (
                <IconButton
                  onClick={() => setIsMinimized(true)}
                  onPointerDown={(e) => e.stopPropagation()}
                  aria-label='minimize'
                  size='small'
                  sx={{ color: 'black', p: 0.5, mr: 0.5 }}>
                  <span
                    style={{
                      fontWeight: 'bold',
                      fontSize: '1.2rem',
                      transform: 'translateY(-4px)',
                    }}>
                    _
                  </span>
                </IconButton>
              )}

              <IconButton
                onClick={toggleMaximize}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label={isMaximized ? 'restore' : 'maximize'}
                size='small'
                sx={{ color: 'black', p: 0.5, mr: 0.5 }}>
                {isMaximized ? (
                  <FilterNoneIcon fontSize='small' sx={{ fontSize: '1.1rem' }} />
                ) : (
                  <CropSquareIcon fontSize='small' sx={{ fontSize: '1.2rem' }} />
                )}
              </IconButton>

              <IconButton
                onClick={handleCloseAttempt}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label='close'
                size='small'
                sx={{ color: 'black', p: 0.5 }}>
                <CloseIcon fontSize='small' />
              </IconButton>
            </Box>
          </Box>

          {/* MAIN BODY */}
          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
            {/* LEFT SIDEBAR */}
            <Box
              sx={{
                width: '150px',
                bgcolor: '#707070',
                flexShrink: 0,
                py: 0,
              }}>
              <List component='nav' disablePadding>
                {menuItems.map((item) => (
                  <ListItemButton
                    key={item.id}
                    selected={activeTab === item.id}
                    onClick={() => handleTabChange(item.id)}
                    sx={{
                      color: 'white',
                      borderBottom: '1px solid white',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                      },
                      '&.Mui-selected': {
                        bgcolor: '#e3f2fd',
                        '& .MuiTypography-root': {
                          fontWeight: 600,
                          color: '#2c23e0',
                        },
                        '&:hover': {
                          bgcolor: '#bbdefb',
                        },
                      },
                    }}>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontSize: '0.9rem' }}>{item.label}</Typography>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            <Divider orientation='vertical' flexItem sx={{ borderColor: 'black' }} />

            {/* RIGHT CONTENT */}
            <Box sx={{ flexGrow: 1, p: 0, overflowY: 'auto', bgcolor: '#fff' }}>
              {renderContent()}
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* ACTION CONFIRMATION DIALOG */}
      <ConfirmDialog
        open={showCloseDialog}
        onClose={handleCancelAction}
        onConfirm={handleConfirmAction}
        message={
          hasKmlQueue
            ? 'You still have unimported markers in the queue. Closing will discard them. Are you sure?'
            : 'Cancel will discard your data and session. Are you sure?'
        }
        severity='warning'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
        }}
      />
    </>
  );
}
